import { memo, useEffect, useRef, useState } from "react";
import * as css from "./merchant-customizable-modal.module.css";

export const MerchantCustomizableModal = memo(
  function MerchantCustomizableModal({
    open,
    onClose,
    children,
  }: {
    open: boolean;
    onClose: () => void;
    children?: React.ReactNode;
  }) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    function closeModal() {
      dialogRef.current?.close();
      setIsOpen(false);
      onClose();
    }

    function openModal() {
      dialogRef.current?.showModal();
      setIsOpen(true);
    }

    useEffect(() => {
      if (open) {
        openModal();
      } else if (isOpen) {
        closeModal();
      }

      const handleClickOutside = (e: MouseEvent) => {
        if (
          dialogRef.current &&
          !dialogRef.current.contains(e.target as Node)
        ) {
          closeModal();
        }
      };

      const handleEscapeKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          closeModal();
        }
      };

      if (open) {
        // without setTimeout, the click event triggers immediately
        setTimeout(() => {
          document.addEventListener("click", handleClickOutside);
          document.addEventListener("keydown", handleEscapeKey);
        }, 0);
      }

      return () => {
        document.removeEventListener("click", handleClickOutside);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }, [open, onClose]);

    const stopPropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
      if (dialogRef.current && e.target === dialogRef.current) {
        closeModal();
      }
    };

    return (
      <dialog
        className={css.dialog}
        onClick={handleBackdropClick}
        ref={dialogRef}
      >
        <div onClick={stopPropagation}>{children}</div>
      </dialog>
    );
  },
);
